import"./index-8p04sssx.mjs";
import {
  createColorMatrixImplementations
} from "./index-cdp7tr8x.mjs";
import {
  assertOptionalFiniteNumber,
  validateUnitInterval,
  parseColorRgba
} from "./index-wxhvgv2s.mjs";
import {
  assertEffectParamsObject,
  assertRequiredColor
} from "./index-4fvp93ps.mjs";

// src/tint.ts
import { Internals } from "remotion";
var { createEffect } = Internals;
var DEFAULT_AMOUNT = 0.5;
var tintSchema = {
  color: {
    type: "color",
    default: undefined,
    description: "Color"
  },
  amount: {
    type: "number",
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_AMOUNT,
    description: "Amount",
    hiddenFromList: false
  }
};
var resolve = (p) => ({
  color: p.color,
  amount: p.amount ?? DEFAULT_AMOUNT
});
var validateTintParams = (params) => {
  assertEffectParamsObject(params, "Tint");
  assertRequiredColor(params.color, "color");
  assertOptionalFiniteNumber(params.amount, "amount");
  if (params.amount !== undefined) {
    validateUnitInterval(params.amount, "amount");
  }
};
var colorContext = null;
var cachedColor = null;
var shaderImplementations = createColorMatrixImplementations("tint", (params) => {
  if (!colorContext) {
    const colorCanvas = document.createElement("canvas");
    colorCanvas.width = 1;
    colorCanvas.height = 1;
    colorContext = colorCanvas.getContext("2d", { willReadFrequently: true });
    if (!colorContext)
      throw new Error("Failed to acquire 2D context for color parsing");
  }
  if (cachedColor?.key !== params.color)
    cachedColor = {
      key: params.color,
      value: parseColorRgba(colorContext, params.color)
    };
  const color = cachedColor.value;
  const amount = (params.amount ?? DEFAULT_AMOUNT) * color[3] / 255;
  return new Float32Array([
    1 - amount,
    0,
    0,
    color[0] / 255 * amount,
    0,
    1 - amount,
    0,
    color[1] / 255 * amount,
    0,
    0,
    1 - amount,
    color[2] / 255 * amount,
    0,
    0,
    0,
    0
  ]);
});
var tint = createEffect({
  type: "dev.remotion.effects.tint",
  label: "tint()",
  documentationLink: "https://www.remotion.dev/docs/effects/tint",
  calculateKey: (params) => {
    const r = resolve(params);
    return `tint-${r.color}-${r.amount}`;
  },
  implementations: {
    ...shaderImplementations,
    "2d": Internals.createEffectImplementation({
      setup: () => null,
      apply: ({ source, target, width, height, params }) => {
        const ctx = target.getContext("2d");
        if (!ctx) {
          throw new Error("Failed to acquire 2D context for tint effect. The canvas may have been assigned a different context type.");
        }
        const r = resolve(params);
        const amount = Math.max(0, Math.min(1, r.amount));
        ctx.clearRect(0, 0, width, height);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
        ctx.drawImage(source, 0, 0, width, height);
        ctx.globalAlpha = amount;
        ctx.globalCompositeOperation = "source-atop";
        ctx.fillStyle = r.color;
        ctx.fillRect(0, 0, width, height);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
      },
      cleanup: () => {
        return;
      }
    })
  },
  schema: tintSchema,
  validateParams: validateTintParams
});
export {
  tint,
  tintSchema
};
